import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';

const TempSlider = ({
  value,
  onChange,
  onApply,
  min = 0,
  max = 120,
  unit = 'C',
  disabled = false,
  programOptions = [],
  selectedProgram,
  onProgramChange,
  applyLabel = 'APPLY',
}) => {
  const [localValue, setLocalValue] = useState(value ?? min);
  const displayValue = value ?? localValue;

  useEffect(() => {
    const nextValue = value ?? min;
    const clamped = Math.max(min, Math.min(max, Number(nextValue) || min));
    setLocalValue(clamped);
  }, [value, min, max]);

  const handleSliderChange = useCallback((e) => {
    const newVal = Number(e.target.value);
    setLocalValue(newVal);
    onChange?.(newVal);
  }, [onChange]);

  const handleStep = useCallback((delta) => {
    const newVal = Math.max(min, Math.min(max, displayValue + delta));
    setLocalValue(newVal);
    onChange?.(newVal);
  }, [displayValue, min, max, onChange]);

  const handleInputChange = useCallback((e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (raw === '') {
      setLocalValue(min);
      onChange?.(min);
      return;
    }

    const parsed = Math.max(min, Math.min(max, Number(raw)));
    setLocalValue(parsed);
    onChange?.(parsed);
  }, [min, max, onChange]);

  const fillPercent = max === min ? 0 : ((displayValue - min) / (max - min)) * 100;

  return (
    <Wrapper>
      <HeaderRow>
        <Label>Set Temp ({unit})</Label>
        {programOptions.length > 0 && (
          <SegmentedControl aria-label="Temperature program">
            {programOptions.map((option) => (
              <ProgramButton
                key={option.value}
                type="button"
                $active={selectedProgram === option.value}
                onClick={() => onProgramChange?.(option.value)}
                disabled={disabled}
              >
                {option.label}
              </ProgramButton>
            ))}
          </SegmentedControl>
        )}
      </HeaderRow>

      <SliderRow>
        <StepBtn onClick={() => handleStep(-1)} disabled={disabled || displayValue <= min} aria-label="Decrease">
          <span>-</span>
        </StepBtn>

        <TrackWrapper>
          <RangeInput
            type="range"
            min={min}
            max={max}
            value={displayValue}
            onChange={handleSliderChange}
            disabled={disabled}
            $fill={fillPercent}
          />
        </TrackWrapper>

        <StepBtn onClick={() => handleStep(1)} disabled={disabled || displayValue >= max} aria-label="Increase">
          <span>+</span>
        </StepBtn>

        <ValueInput
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          disabled={disabled}
          aria-label="Temperature value"
        />
      </SliderRow>

      {onApply && (
        <ApplyButton
          type="button"
          onClick={() => onApply(displayValue)}
          disabled={disabled}
        >
          {applyLabel}
        </ApplyButton>
      )}
    </Wrapper>
  );
};

export default TempSlider;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #90a1b9;
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  border: 0.8px solid rgba(59, 130, 246, 0.24);
  background: var(--switch-panel-bg);
`;

const ProgramButton = styled.button`
  min-width: 86px;
  height: 26px;
  padding: 0 10px;
  border: none;
  border-radius: 7px;
  background: ${({ $active }) => ($active ? '#3b82f6' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--switch-text-muted)')};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  background: var(--switch-panel-bg);
  color: var(--switch-text-muted);
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--switch-panel-bg-alt);
    color: var(--switch-text-primary);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const TrackWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const RangeInput = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(
    to right,
    #3b82f6 0%,
    #3b82f6 ${({ $fill }) => $fill}%,
    rgba(51, 65, 85, 0.5) ${({ $fill }) => $fill}%,
    rgba(51, 65, 85, 0.5) 100%
  );
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ValueInput = styled.input`
  width: 52px;
  height: 32px;
  border-radius: 10px;
  border: 0.8px solid rgba(59, 130, 246, 0.3);
  background: var(--switch-panel-bg);
  color: var(--switch-text-primary);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  outline: none;
  flex-shrink: 0;

  &:focus {
    border-color: #3b82f6;
  }

  &:disabled {
    opacity: 0.4;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
